import { animated, config, to, useSprings } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import type { Card, Position } from '@whispers/sdk';
import { useRef, useState } from 'react';

import { getCardUrl } from '../hooks/getCardUrl';

const CARD_HEIGHT = 140;
const CARD_WIDTH = 100;

// ─── Types ────────────────────────────────────────────────────────────────────

type CardSpringTarget = {
  x: number;
  y: number;
  xRot: number;
  yRot: number;
  zRot: number;
  zIndex: number;
  scale: number;
  delay?: number;
};

// ─── Position helpers ─────────────────────────────────────────────────────────

// 3D card rotation transform string
const trans = (xR: number, yR: number, zR: number, s: number) =>
  `perspective(1500px) rotateX(${xR}deg) rotateY(${yR}deg) rotateZ(${zR}deg) scale(${s})`;

// Resting position in the draw pile (stacked at bottom-centre)
const getPilePosition = (
  { width, height }: { width: number; height: number },
  index: number,
): CardSpringTarget => ({
  x: width / 2 - CARD_WIDTH / 2,
  y: index * 6 + height - CARD_HEIGHT * 2,
  xRot: 0,
  yRot: 180, // face-down
  zRot: -10 + Math.random() * 20,
  zIndex: index,
  scale: 1,
  delay: index * 100,
});

// Off-screen entry position (cards fly in from below on mount / shuffle)
const getOffScreenPosition = ({ width }: { width: number }, index: number): CardSpringTarget => ({
  x: width / 2 - CARD_WIDTH / 2,
  y: 1000,
  xRot: 0,
  yRot: 180,
  zRot: 0,
  zIndex: index,
  scale: 2,
});

// Pixel position of a spread slot on screen
const getSpreadPosition = (
  position: Position,
  { width, height }: { width: number; height: number },
) => ({
  x: position.x + width / 2 - CARD_WIDTH / 2,
  y: position.y + height / 2 - CARD_HEIGHT / 2,
  rotate: position.rotate ?? 0,
});

// ─── Component ────────────────────────────────────────────────────────────────

export type DeckProps = {
  cards: Card[];
  deckSlug: string;
  positions: Position[];
  height: number;
  width: number;
  onExpanded?: (params?: { card: Card; position: Position }) => void;
  onComplete?: () => void;
};

export const Deck = ({
  cards,
  deckSlug,
  positions,
  height,
  width,
  onExpanded,
  onComplete,
}: DeckProps) => {
  const drawnRef = useRef(new Set<number>());
  const expanded = useRef(-1);
  const dims = { width, height };

  // Committed resting target per card. Starts at pile position; updated to the
  // spread position once drawn so parent re-renders never reset drawn cards.
  const cardTargets = useRef(cards.map((_, i) => getPilePosition(dims, i)));

  // Incrementing this causes useSprings to re-read cardTargets after markDrawn.
  const [renderCount, setRenderCount] = useState(0);

  const markDrawn = (index: number, position: Position) => {
    drawnRef.current.add(index);
    const { x, y, rotate } = getSpreadPosition(position, dims);
    cardTargets.current[index] = {
      ...getPilePosition(dims, index),
      x,
      y,
      zRot: rotate,
      yRot: 0,
      zIndex: cards.length - index,
    };
    setRenderCount((n) => n + 1);
  };

  const getImage = (i: number, yRot: number) => {
    if (yRot < 90) {
      const cardIndex = cards.length - 1 - i;
      return `url(${getCardUrl({ slug: cards[cardIndex]!.slug, deckSlug })})`;
    }
    return `url(${getCardUrl({ deckSlug })})`;
  };

  // useSprings reads cardTargets so every re-render applies the correct resting
  // position — pile for undrawn cards, spread slot for drawn ones.
  const [props, api] = useSprings(
    cards.length,
    (i) => ({ ...cardTargets.current[i]!, config: config.default }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [renderCount, cards.length, width, height],
  );

  const dragOrigin = useRef<[number, number]>([0, 0]);

  // ─── Gesture sub-handlers ──────────────────────────────────────────────────

  const handleShuffle = () => {
    void api.start((i) => ({
      ...getPilePosition(dims, i),
      from: getOffScreenPosition(dims, i),
      config: config.default,
    }));
  };

  const handleDrag = (index: number, x: number, y: number, zRot: number, isExpanded: boolean) => {
    const scale = isExpanded ? 2.5 : 1.5;
    void api.start((i) => {
      if (i !== index) return undefined;
      return {
        x,
        y,
        yRot: 0,
        zRot,
        scale,
        zIndex: cards.length,
        immediate: (key: string) => key !== 'yRot',
      };
    });
  };

  const handleExpand = (index: number, zRot: number) => {
    const positionIndex = cards.length - 1 - index;
    const position = positions[positionIndex];
    const card = cards[positionIndex];

    if (card && position) {
      onExpanded?.({ card, position });
    } else {
      onExpanded?.();
    }

    void api.start((i) => {
      if (i !== index) return undefined;
      return {
        x: width / 2 - CARD_WIDTH / 2,
        y: height / 2 - CARD_HEIGHT / 2,
        yRot: 0,
        zRot,
        scale: 3,
        zIndex: cards.length,
        immediate: (key: string) => key === 'zIndex',
      };
    });
  };

  const handleRelease = (index: number, wasTapped: boolean) => {
    onExpanded?.(undefined);

    void api.start((i) => {
      const positionIndex = cards.length - 1 - i;
      const position = positions[positionIndex];
      const {
        x: px,
        y: py,
        rotate,
      } = position ? getSpreadPosition(position, dims) : { x: 0, y: 0, rotate: 0 };

      // Active card — animate to spread slot
      if (i === index) {
        // Tapped: slide face-down to position, then flip face-up
        if (wasTapped) {
          return {
            to: [{ x: px, y: py, yRot: 180, zRot: rotate, scale: 1 }, { yRot: 0 }],
            zIndex: cards.length - i,
            immediate: (key: string) => key === 'zIndex',
          };
        }
        // Dragged: already face-up, just spring to position
        return {
          x: px,
          y: py,
          yRot: 0,
          zRot: rotate,
          scale: 1,
          zIndex: cards.length - i,
          immediate: (key: string) => key === 'zIndex',
        };
      }

      // Other drawn cards — hold at their spread positions
      if (drawnRef.current.has(i)) {
        return {
          x: px,
          y: py,
          yRot: 0,
          zRot: rotate,
          scale: 1,
          zIndex: cards.length - i,
          immediate: (key: string) => key === 'zIndex',
        };
      }

      return undefined;
    });
  };

  // ─── Drag binding ──────────────────────────────────────────────────────────

  const bind = useDrag<{ args: [number] }>(
    ({ args: [idx], active, first, movement: [mx, my], direction: [xDir], tap }): void => {
      const index = Number(idx);
      const drawn = drawnRef.current;
      const isDrawn = drawn.has(index);
      const isTopOfPile = cards.length - 1 - drawn.size === index;

      // Tap on a non-top card with nothing drawn → shuffle
      if (!drawn.size && tap && !isTopOfPile) {
        handleShuffle();
        return;
      }

      // Ignore buried cards
      if (!isDrawn && !isTopOfPile) return;

      // Capture card position at the start of each gesture
      if (first) {
        dragOrigin.current = [props[index]?.x.get() ?? 0, props[index]?.y.get() ?? 0];
      }

      // Toggle expand on tap for already-drawn cards
      if (tap && isDrawn) {
        expanded.current = expanded.current === index ? -1 : index;
      }

      // Mark drawn once intentionally moved or tapped
      const hasMoved = Math.abs(mx) > 5 || Math.abs(my) > 5;
      const positionIndex = cards.length - 1 - index;
      const activePosition = positions[positionIndex];
      if (!isDrawn && (hasMoved || tap) && activePosition) {
        markDrawn(index, activePosition);
      }

      if (drawnRef.current.size === cards.length) onComplete?.();

      const x = dragOrigin.current[0] + mx;
      const y = dragOrigin.current[1] + my;
      const zRot = mx / 100 + xDir * 1.5;
      const isExpanded = expanded.current === index;

      if (active) {
        handleDrag(index, x, y, zRot, isExpanded);
        return;
      }

      if (isExpanded) {
        handleExpand(index, zRot);
        return;
      }

      handleRelease(index, tap);
    },
    { filterTaps: true },
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {props.map(({ x, y, xRot, yRot, zRot, scale, zIndex }, i) => (
        <animated.div
          key={i}
          className="fixed will-change-transform"
          style={{ x, y, zIndex, width: CARD_WIDTH, height: CARD_HEIGHT }}
        >
          <animated.div
            {...bind(i)}
            className="h-full w-full touch-none rounded-lg bg-white bg-cover bg-center bg-no-repeat shadow-lg will-change-transform"
            style={{
              touchAction: 'none',
              transform: to([xRot, yRot, zRot, scale], trans),
              backgroundImage: to([yRot], (yr) => getImage(i, yr)),
            }}
          />
        </animated.div>
      ))}
    </>
  );
};
